import type { QueryClient } from "@tanstack/react-query";
import { pendingSignalCountKey } from "@/hooks/usePendingSignalCount";
import type { RealtimeHandler } from "../../api/realtimeHandlers";
import { marketSignalsKey, reconcileSignals } from "./signalsCache";
import { fieldReportsKey } from "./useFieldReports";

/**
 * Market signals: which cached reads each realtime hint touches.
 *
 * Hints can come in a run (a teammate triaging the phone deck, the agent filing
 * several signals in one turn), so they are coalesced: brands pile up for
 * COALESCE_MS after the last hint, never longer than MAX_WAIT_MS after the
 * first, then refresh once. The list goes through its write gate
 * (`reconcileSignals`): while this tab has a signal write in flight, the
 * refresh is held and the last write to settle does it.
 */

const COALESCE_MS = 250;
const MAX_WAIT_MS = 1_000;

type Batch = {
  qc: QueryClient;
  signals: Set<string>;
  reports: Set<string>;
  startedAt: number;
  timer?: ReturnType<typeof setTimeout>;
};

let batch: Batch | null = null;

function queue(qc: QueryClient, brandId: string, work: { signals: boolean; reports: boolean }) {
  const now = Date.now();
  batch ??= { qc, signals: new Set(), reports: new Set(), startedAt: now };
  if (work.signals) batch.signals.add(brandId);
  if (work.reports) batch.reports.add(brandId);
  clearTimeout(batch.timer);
  const wait = Math.max(0, Math.min(COALESCE_MS, batch.startedAt + MAX_WAIT_MS - now));
  batch.timer = setTimeout(flush, wait);
}

function flush() {
  const current = batch;
  batch = null;
  if (!current) return;
  const { qc } = current;
  for (const brandId of current.signals) reconcileSignals(qc, brandId);
  for (const brandId of current.reports) {
    void qc.invalidateQueries({ queryKey: fieldReportsKey(brandId) });
  }
}

export const SIGNALS_REALTIME: RealtimeHandler = {
  apply(qc, event) {
    switch (event.type) {
      case "signals.changed":
        queue(qc, event.brandId, { signals: true, reports: false });
        return;
      case "signals.report.filed":
        // A report with nothing new to triage still changes the Reports trail.
        queue(qc, event.brandId, {
          signals: event.data.signalIds.length > 0,
          reports: true,
        });
        return;
    }
  },

  resyncKeys: (brandId) => [
    marketSignalsKey(brandId),
    pendingSignalCountKey(brandId),
    fieldReportsKey(brandId),
  ],
};
